import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cpu, Shield, ArrowRight, AlertTriangle, Unlock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { fehlerText, API, demoLogin } from "@/lib/api";
import { toast } from "sonner";

// The backend owns the whole OAuth handshake: it redirects to Google, takes
// the code back on its own callback, and only then bounces the browser to
// /auth/callback with a session token in the fragment. The redirect URI is
// configured server-side (GOOGLE_REDIRECT_URI) and must match the one
// registered on the Google OAuth client — never build it here.
function startLogin() {
  window.location.href = `${API}/auth/google/login`;
}

export default function Landing({ rejected = false }) {
  // Read auth_error from URL so the user sees WHY the login looped.
  const params = new URLSearchParams(window.location.search || "");
  const authError = params.get("auth_error");
  const authErrorMsg = params.get("msg");
  const { acceptLogin } = useAuth();
  const navigate = useNavigate();
  const [demoBusy, setDemoBusy] = useState(false);
  // Which login paths this deployment actually offers. Reported by the
  // backend so the UI never promises a Google button that answers 503.
  const [authCfg, setAuthCfg] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/auth/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { if (alive) setAuthCfg(cfg); })
      .catch(() => { /* leave null — buttons fall back to "unknown" */ });
    return () => { alive = false; };
  }, []);

  const onDemoLogin = async () => {
    setDemoBusy(true);
    try {
      const u = await demoLogin();
      // Persists the Bearer fallback token AND sets the user — see
      // AuthContext.acceptLogin. Without this, Safari with ITP would
      // lose the session on the very next navigation.
      acceptLogin(u);
      toast.success("Demo admin session active");
      navigate("/admin", { replace: true, state: { user: u } });
    } catch (err) {
      toast.error(fehlerText(err) || err?.message || "Demo login failed");
    } finally {
      setDemoBusy(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="absolute inset-0 tactical-grid opacity-30 pointer-events-none" />
      <div className="absolute inset-0 scanlines opacity-50 pointer-events-none" />

      <header className="relative z-10 px-8 py-5 flex items-center justify-between border-b border-border/50">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Cpu className="w-5 h-5 text-primary" strokeWidth={2.25} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black uppercase text-sm tracking-wider text-foreground">
              Frisbee/X · Tactic-Cut
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Admin Console · Internal
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-8 pt-24 pb-16">
        {/* Auth-Error banner stays collapsed by default — a failed login is
            usually self-explanatory, the detail is for debugging. */}
        {authError && !rejected && (
          <details className="mb-6 border border-border/60 bg-card/40 rounded-sm">
            <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" /> Previous Google sign-in failed · click to inspect
            </summary>
            <div className="px-3 py-2 border-t border-border/60">
              <AuthErrorBanner code={authError} msg={authErrorMsg} />
            </div>
          </details>
        )}
        {rejected ? (
          <RejectedScreen />
        ) : (
          <SignInScreen onSignIn={startLogin} onDemoLogin={onDemoLogin} demoBusy={demoBusy} authCfg={authCfg} />
        )}
      </main>
    </div>
  );
}

function AuthErrorBanner({ code, msg }) {
  const isCookie = code === "cookie_dropped";
  const isExchange = code === "exchange_failed";
  return (
    <div data-testid="auth-error-banner" className="mb-8 border border-destructive/50 bg-destructive/10 rounded-sm p-5">
      <div className="font-mono text-[10px] uppercase tracking-widest text-destructive mb-2 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5" /> Login interrupted · {code}
      </div>
      {isCookie && (
        <div className="text-sm text-foreground/90 leading-relaxed">
          The Google sign-in succeeded but your browser <span className="text-destructive font-semibold">silently dropped the session cookie</span>. This usually happens when:
          <ul className="mt-2 ml-4 list-disc space-y-1 text-foreground/80">
            <li>3rd-party cookies are blocked for this domain (Safari ITP / Brave / Strict mode)</li>
            <li>A privacy extension (Ghostery, Privacy Badger, uBlock with cookie list) is active</li>
            <li>Browser is in private/incognito mode with strict cookie policy</li>
          </ul>
          <div className="mt-3 text-foreground/70 font-mono text-xs">
            Quick fix: try a normal Chrome/Edge window, or allow cookies for this site.
          </div>
        </div>
      )}
      {isExchange && (
        <div className="text-sm text-foreground/90 leading-relaxed">
          The session exchange with Google failed.
          <div className="mt-2 font-mono text-xs bg-card border border-border rounded-sm p-2 text-muted-foreground break-all">
            {msg || "(no detail)"}
          </div>
          <div className="mt-2 text-foreground/70 text-xs">
            Open DevTools → Network → click the row labelled <span className="text-primary font-mono">auth/google/callback</span> and share the response if this persists.
          </div>
        </div>
      )}
      {!isCookie && !isExchange && msg && (
        <div className="font-mono text-xs text-muted-foreground break-all">{msg}</div>
      )}
    </div>
  );
}

function SignInScreen({ onSignIn, onDemoLogin, demoBusy, authCfg }) {
  const googleEnabled = authCfg?.google_enabled !== false;
  return (
    <>
      <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-4 flex items-center gap-2">
        <Shield className="w-3.5 h-3.5" /> Admin · Internal Tool
      </div>
      <h1 className="font-display font-black uppercase text-5xl sm:text-6xl tracking-tight leading-[0.95] mb-6">
        Tactic Cutter<span className="text-primary">.</span>
      </h1>
      <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-xl">
        Internal admin console for building ML training data from ultimate frisbee match
        recordings. Cut, calibrate, track and label sequences. Restricted access.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
        <Button
          data-testid="demo-login-button"
          onClick={onDemoLogin}
          disabled={demoBusy}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest text-base px-6 py-6 h-auto flex-1 disabled:opacity-50"
        >
          {demoBusy ? (
            <><Loader2 className="w-5 h-5 mr-3 animate-spin" /> Signing in…</>
          ) : (
            <><Unlock className="w-5 h-5 mr-3" /> Mit Demo-Admin einloggen</>
          )}
        </Button>
        <Button
          data-testid="login-button"
          onClick={onSignIn}
          disabled={!googleEnabled}
          variant="outline"
          className="border-border bg-secondary hover:bg-accent text-foreground font-display font-bold uppercase tracking-widest text-sm px-5 py-6 h-auto disabled:opacity-40"
        >
          Google
        </Button>
      </div>

      <div
        data-testid="auth-mode-hint"
        className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground max-w-xl"
      >
        {googleEnabled
          ? "Google-Anmeldung aktiv · Zugang nur für Adressen auf der Admin-Allowlist."
          : "⚠ Google-Anmeldung nicht konfiguriert (GOOGLE_CLIENT_ID/SECRET fehlen) — bis dahin Demo-Admin nutzen."}
      </div>

      <div className="mt-12 border border-border bg-card/60 backdrop-blur rounded-sm p-5 max-w-xl">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Are you a player?
        </div>
        <div className="text-sm text-foreground/85 leading-relaxed">
          The player-facing analysis product is a separate app called <span className="text-primary font-semibold">frisbee-tactics</span> — coming soon.
          This console here is for the data team only.
        </div>
      </div>
    </>
  );
}

function RejectedScreen() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signOutAndRetry = async () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${backend}/api/auth/logout`, { method: "POST", credentials: "include" });
    window.location.href = "/";
  };
  return (
    <>
      <div className="font-mono text-[10px] uppercase tracking-widest text-destructive mb-4 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5" /> Access Denied
      </div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight leading-[0.95] mb-6">
        Wrong door<span className="text-destructive">.</span>
      </h1>
      <p className="text-base text-muted-foreground leading-relaxed mb-3 max-w-xl">
        Your account is not authorized for the <span className="text-foreground font-semibold">Tactic-Cut Admin Console</span>.
      </p>
      <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-xl">
        If you&apos;re a player looking to analyze your matches, the consumer product
        you want is <span className="text-primary font-semibold">frisbee-tactics</span> — it&apos;s a separate app.
      </p>
      <Button
        data-testid="logout-and-retry"
        onClick={signOutAndRetry}
        variant="outline"
        className="border-border bg-card hover:bg-accent hover:text-primary"
      >
        Sign out & try a different account
      </Button>
    </>
  );
}
